import React, { useCallback, useEffect, useMemo, useState } from "react";
import { applyNodeChanges, Controls, ReactFlow, useNodes, useReactFlow, MiniMap } from "@xyflow/react";

// Contexts
import { MapContext, useMasteryMap } from "../../context";
import { useStudyPathSideBar } from "../../../StudyPathSideBar/context";

// Components
import ProcessingModal from "../../../../components/ProcessingModal";
import CourseNode from "../Map/CourseNode";
import UnitNode from "../Map/UnitNode";
import SmartNote from "../Map/SmartNote";
import CustomEdge from "../Map/CustomEdge";

// Styles
import styles from "./map.module.scss";
import '@xyflow/react/dist/style.css';

// Utils
import { mockSmartNotes } from "../../../../utils/mocks";
// import { smartNoteExpandUrl } from "../../../../utils/urls";

const nodeTypes = {
  course: CourseNode,
  unit: UnitNode,
  subUnit: SmartNote,
}

const edgeTypes = {
  custom: CustomEdge,
}

function layoutTree(allNodes) {
  const rootNode = allNodes.find(node => node.data.level === 0 && node.data.parent === null);

  let nextY = 0;
  const verticalGap = 40; // Space between nodes

  function dfs(node, depth = 0) {
    const children = allNodes.filter(n => n.data.parent === node.id);
    const nodeHeight = node.measured?.height || 100; // Fallback if not measured

    // Set x by depth
    node.position.x = depth * (node.measured.width + 100);

    if (children.length === 0) {
      // Leaf node: assign next available y
      node.position.y = nextY;
      nextY += nodeHeight + verticalGap;
      return { minY: node.position.y, maxY: node.position.y + nodeHeight };
    } else {
      // Internal node: layout children, then center this node
      const childBounds = children.map(child => dfs(child, depth + 1));
      const minY = Math.min(...childBounds.map(b => b.minY));
      const maxY = Math.max(...childBounds.map(b => b.maxY));
      node.position.y = minY + (maxY - minY) / 2 - nodeHeight / 2;
      return { minY, maxY };
    }
  }

  if (rootNode) {
    dfs(rootNode, 0);
  }
}

const Map = () => {
  const { course } = useStudyPathSideBar();
  const { units, subUnitsMap } = useMasteryMap();

  const displayedNodes = useNodes();
  const reactFlow = useReactFlow();

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [processing, setProcessing] = useState(false);

  const loadStartingNodes = useCallback(() => {
    const nodes = [];
    const edges = [];

    // Important: Convert all ids to string so that the ReactFlow can work with them

    // Convert course id to string
    const courseId = course.id.toString();
    const CourseNode = {
      id: courseId,
      type: "course",
      position: { x: 0, y: 0 },
      data: {
        id: courseId,
        label: course.title,
        progress: Math.floor(Math.random() * 101),
        level: 0,
        parent: null
      }
    }

    nodes.push(CourseNode);

    // Units
    if (units) {
      units.forEach((unit, unitIdx) => {
        const subUnits = subUnitsMap[unit.id];

        // Convert unit id to string
        const unitId = unit.id.toString();
        const UnitNode = {
          id: unitId,
          type: "unit",
          position: { x: 0, y: 0 },
          data: { ...unit, progress: Math.floor(Math.random() * 101), parent: courseId, level: 1, index: unitIdx }
        }

        nodes.push(UnitNode);
        // Edge from course to unit
        edges.push({ id: `${courseId}--${unitId}`, source: courseId, target: unitId, type: "custom" })

        if (subUnits) {
          subUnits.forEach((subUnit, idx) => {
            // Convert sub unit id to string
            const subUnitId = subUnit.id.toString();
            const subNode = {
              id: subUnitId,
              type: "subUnit",
              position: { x: 0, y: 0 },
              data: {
                ...subUnit,
                id: subUnitId,
                progress: Math.floor(Math.random() * 101),
                expanded: false,
                parent: unitId,
                level: 2
              }
            }
            nodes.push(subNode);

            // Edge from unit to subUnit
            edges.push({ id: `${unitId}--${subUnitId}`, source: unitId, target: subUnitId, type: "custom" })
          })
        }
      })
    }

    setNodes(nodes);
    setEdges(edges);
  }, [units, subUnitsMap]);

  // Methods
  const handleDeepDive = useCallback(async (nodeId) => {
    // TODO: Implement expand
    setProcessing(true);

    const node = nodes.find(node => node.id === nodeId);
    if (node) {
      const nodesCopy = [...nodes];
      const edgesCopy = [...edges];

      // MOCK DATA
      await new Promise(resolve => setTimeout(resolve, 3000));
      const generatedSmartNotes = mockSmartNotes();

      // TODO: Implement Call to the API Create Smart Notes
      // try {
      //   const options = {
      //     method: "POST",
      //     headers: {
      //       "X-CSRFToken": document.querySelector('meta[name="csrf-token"]').content,
      //       "Accept": "application/json",
      //     },
      //   }

      //   const response = await fetch(smartNoteExpandUrl(nodeId), options);
      //   if (response.ok) {
      //     const data = await response.json();

      //     generatedSmartNotes = data.smart_notes;
      //   } else {
      //     console.error("Error", response);
      //   }
      // } catch (error) {
      //   console.error("Error", error);
      // }

      if (generatedSmartNotes.length > 0) {
        const parentNode = nodesCopy.find(node => node.id === nodeId);

        parentNode.data.expanded = true;

        // Add the smart notes to the node
        generatedSmartNotes.forEach((smartNote, idx) => {
          nodesCopy.push({
            id: `${smartNote.id}`,
            type: "subUnit",
            position: { x: 0, y: 0 },
            data: {
              ...smartNote,
              progress: Math.floor(Math.random() * 101),
              parent: parentNode.id,
              level: parentNode.data.level + 1
            }
          });

          // Add the smart notes to the edges
          edgesCopy.push({
            id: `${nodeId}--${smartNote.id}`,
            source: parentNode.id,
            target: `${smartNote.id}`,
            type: "custom"
          });
        });

        // Update the node with the smart notes
        setNodes(nodesCopy);
        setEdges(edgesCopy);
      }
    }

    setProcessing(false);
  }, [nodes, edges]);

  const handlePractice = useCallback((nodeId) => {
    // TODO: Keith Implement practice Navigation
  }, []);

  useEffect(() => {
    loadStartingNodes();
  }, [loadStartingNodes]);

  // Handlers
  const onNodesChange = useCallback((changes) => {
    const newNodes = applyNodeChanges(changes, nodes);

    layoutTree(newNodes);

    const newNodesCopy = newNodes.map(node => ({ ...node, position: { ...node.position } }));

    setNodes(newNodesCopy);
  }, [nodes]);

  // Context
  const contextValue = useMemo(() => ({
    onDeepDive: handleDeepDive,
    onPractice: handlePractice,
  }), [handleDeepDive, handlePractice]);

  return (
    <MapContext.Provider value={contextValue}>
      <div className={styles.mapContainer}>
        <ReactFlow
          nodes={nodes}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          edges={edges}
          edgeTypes={edgeTypes}
          fitView
          nodesDraggable={false}
        >
          <Controls />

          <MiniMap />
        </ReactFlow>
      </div>

      <ProcessingModal open={processing} />
    </MapContext.Provider>
  );
};

export default Map;
